import { FixedPhraseRootReference, IFixedPhraseRepository } from "../../../domain/document";
import { IContentSource } from "../../../domain/drawing";
import { FixedPhraseTranslator, FixedPhraseExportSource } from ".";
import { AuthenticatedService } from "../..";
import { ManagementTokenPublishResult } from "../../../domain/system";
import { ManagementTokenTranslator } from "../../system";

export class FixedPhraseRepository extends AuthenticatedService implements IFixedPhraseRepository {
  constructor(getToken: () => string) {
    super(getToken);
  }

  private translator = new FixedPhraseTranslator();

  private isEmpty(value: string) {
    return (value === undefined || value === null || value === "");
  }

  async getUserFixedPhrase(id: string): Promise<FixedPhraseRootReference> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    return this.getFixedPhrase("/api/templatephrase/user/" + id);
  }

  async getDepartmentFixedPhrase(id: string): Promise<FixedPhraseRootReference> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    return this.getFixedPhrase("/api/templatephrase/department/" + id);
  }

  async getFormatFixedPhrase(id: string): Promise<FixedPhraseRootReference> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    return this.getFixedPhrase("/api/templatephrase/format/" + id);
  }

  async getTemporarySaveUserFixedPhrase(id: string): Promise<FixedPhraseRootReference> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    return this.getFixedPhrase("/api/templatephrase/temporary-save/user/" + id);
  }

  async getReceptionUserFixedPhrase(id: string): Promise<FixedPhraseRootReference> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    return this.getFixedPhrase("/api/templatephrase/reception/user/" + id);
  }

  async getReceptionDepartmentFixedPhrase(id: string): Promise<FixedPhraseRootReference> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    return this.getFixedPhrase("/api/templatephrase/reception/department/" + id);
  }

  async getCommonDepartmentFixedPhrase(): Promise<FixedPhraseRootReference> {
    return this.getFixedPhrase("/api/templatephrase/common-department/");
  }

  async getAiUserFixedPhrase(id: string): Promise<FixedPhraseRootReference> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    return this.getFixedPhrase("/api/templatephrase/ai/user/" + id);
  }

  async getAiDepartmentFixedPhrase(id: string): Promise<FixedPhraseRootReference> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    return this.getFixedPhrase("/api/templatephrase/ai/department/" + id);
  }

  async getAiFormatFixedPhrase(id: string): Promise<FixedPhraseRootReference> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    return this.getFixedPhrase("/api/templatephrase/ai/format/" + id);
  }

  async getAiCommonDepartmentFixedPhrase(): Promise<FixedPhraseRootReference> {
    return this.getFixedPhrase("/api/templatephrase/ai/common-department/");
  }

  private async getFixedPhrase(url: string): Promise<FixedPhraseRootReference> {
    const result = await this.get(url);
    return this.translator.toFixedPhraseTree(() => this.token, result.data);
  }

  async setUserFixedPhrase(fixedPhrase: FixedPhraseRootReference): Promise<string> {
    if (this.isEmpty(fixedPhrase.templateId)) { throw new Error("templateId is empty!!!"); }
    return this.setFixedPhrase("/api/templatephrase/user/save", fixedPhrase);
  }

  async setDepartmentFixedPhrase(fixedPhrase: FixedPhraseRootReference): Promise<string> {
    if (this.isEmpty(fixedPhrase.templateId)) { throw new Error("templateId is empty!!!"); }
    return this.setFixedPhrase("/api/templatephrase/department/save", fixedPhrase);
  }

  async setFormatFixedPhrase(fixedPhrase: FixedPhraseRootReference): Promise<string> {
    if (this.isEmpty(fixedPhrase.templateId)) { throw new Error("templateId is empty!!!"); }
    return this.setFixedPhrase("/api/templatephrase/format/save", fixedPhrase);
  }

  async setTemporarySaveUserFixedPhrase(fixedPhrase: FixedPhraseRootReference): Promise<string> {
    if (this.isEmpty(fixedPhrase.templateId)) { throw new Error("templateId is empty!!!"); }
    return this.setFixedPhrase("/api/templatephrase/temporary-save/user/save", fixedPhrase);
  }

  async setReceptionUserFixedPhrase(fixedPhrase: FixedPhraseRootReference): Promise<string> {
    if (this.isEmpty(fixedPhrase.templateId)) { throw new Error("templateId is empty!!!"); }
    return this.setFixedPhrase("/api/templatephrase/reception/user/save", fixedPhrase);
  }

  async setReceptionDepartmentFixedPhrase(fixedPhrase: FixedPhraseRootReference): Promise<string> {
    if (this.isEmpty(fixedPhrase.templateId)) { throw new Error("templateId is empty!!!"); }
    return this.setFixedPhrase("/api/templatephrase/reception/department/save", fixedPhrase);
  }

  async setCommonDepartmentFixedPhrase(fixedPhrase: FixedPhraseRootReference): Promise<string> {
    return this.setFixedPhrase("/api/templatephrase/common-department/save", fixedPhrase);
  }

  async setAiUserFixedPhrase(fixedPhrase: FixedPhraseRootReference): Promise<string> {
    if (this.isEmpty(fixedPhrase.templateId)) { throw new Error("templateId is empty!!!"); }
    return this.setFixedPhrase("/api/templatephrase/ai/user/save", fixedPhrase);
  }

  async setAiDepartmentFixedPhrase(fixedPhrase: FixedPhraseRootReference): Promise<string> {
    if (this.isEmpty(fixedPhrase.templateId)) { throw new Error("templateId is empty!!!"); }
    return this.setFixedPhrase("/api/templatephrase/ai/department/save", fixedPhrase);
  }

  async setAiFormatFixedPhrase(fixedPhrase: FixedPhraseRootReference): Promise<string> {
    if (this.isEmpty(fixedPhrase.templateId)) { throw new Error("templateId is empty!!!"); }
    return this.setFixedPhrase("/api/templatephrase/ai/format/save", fixedPhrase);
  }

  async setAiCommonDepartmentFixedPhrase(fixedPhrase: FixedPhraseRootReference): Promise<string> {
    return this.setFixedPhrase("/api/templatephrase/ai/common-department/save", fixedPhrase);
  }

  async setFixedPhrase(url: string, fixedPhrase: FixedPhraseRootReference): Promise<string> {
    const fixedPhraseBean = this.translator.toFixedPhraseFolderRootBean(fixedPhrase);
    const result = await this.post(url, fixedPhraseBean);
    const tableId = result.data as number;
    return tableId.toString();
  }

  async getDepartmentFixedPhraseManagementToken(id: string, isForcibly: boolean): Promise<ManagementTokenPublishResult> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    return this.getManagementTokenPublishResult("/api/templatephrase/department/" + id + "/publish-management-token", isForcibly);
  }

  async getReceptionDepartmentFixedPhraseManagementToken(id: string, isForcibly: boolean): Promise<ManagementTokenPublishResult> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    return this.getManagementTokenPublishResult("/api/templatephrase/reception/department/" + id + "/publish-management-token", isForcibly);
  }

  async getFormatFixedPhraseManagementToken(id: string, isForcibly: boolean): Promise<ManagementTokenPublishResult> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    return this.getManagementTokenPublishResult("/api/templatephrase/format/" + id + "/publish-management-token", isForcibly);
  }

  async getCommonDepartmentFixedPhraseManagementToken(isForcibly: boolean): Promise<ManagementTokenPublishResult> {
    return this.getManagementTokenPublishResult("/api/templatephrase/common-department/publish-management-token", isForcibly);
  }

  async getAiDepartmentFixedPhraseManagementToken(id: string, isForcibly: boolean): Promise<ManagementTokenPublishResult> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    return this.getManagementTokenPublishResult("/api/templatephrase/ai/department/" + id + "/publish-management-token", isForcibly);
  }

  async getAiFormatFixedPhraseManagementToken(id: string, isForcibly: boolean): Promise<ManagementTokenPublishResult> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    return this.getManagementTokenPublishResult("/api/templatephrase/ai/format/" + id + "/publish-management-token", isForcibly);
  }

  async getAiCommonDepartmentFixedPhraseManagementToken(isForcibly: boolean): Promise<ManagementTokenPublishResult> {
    return this.getManagementTokenPublishResult("/api/templatephrase/ai/common-department/publish-management-token", isForcibly);
  }

  private async getManagementTokenPublishResult(url: string, isForcibly: boolean): Promise<ManagementTokenPublishResult> {
    const query = {
      params: {
        isForcibly: isForcibly,
      }
    };
    const result = await this.get(url, query);
    return ManagementTokenTranslator.toManagementTokenPublishResult(result.data);
  }

  async exportDepartmentFixedPhrase(id: string, isJsonFile: boolean): Promise<IContentSource> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    const query = {
      params: {
        isJsonFile: isJsonFile,
      }
    };
    const result = await this.get("/api/templatephrase/department/" + id + "/export", query);
    return new FixedPhraseExportSource(() => this.token, result.data);
  }

  async exportFormatFixedPhrase(id: string, isJsonFile: boolean): Promise<IContentSource> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    const query = {
      params: {
        isJsonFile: isJsonFile,
      }
    };
    const result = await this.get("/api/templatephrase/format/" + id + "/export", query);
    return new FixedPhraseExportSource(() => this.token, result.data);
  }

  async exportUserFixedPhrase(id: string, isJsonFile: boolean): Promise<IContentSource> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    const query = {
      params: {
        isJsonFile: isJsonFile,
      }
    };
    const result = await this.get("/api/templatephrase/user/" + id + "/export", query);
    return new FixedPhraseExportSource(() => this.token, result.data);
  }

  async exportCommonDepartmentFixedPhrase(isJsonFile: boolean): Promise<IContentSource> {
    const query = {
      params: {
        isJsonFile: isJsonFile,
      }
    };
    const result = await this.get("/api/templatephrase/common-department/export", query);
    return new FixedPhraseExportSource(() => this.token, result.data);
  }

  async exportTemporarySaveUserFixedPhrase(id: string, isJsonFile: boolean): Promise<IContentSource> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    const query = {
      params: {
        isJsonFile: isJsonFile,
      }
    };
    const result = await this.get("/api/templatephrase/temporary-save/user/" + id + "/export", query);
    return new FixedPhraseExportSource(() => this.token, result.data);
  }

  async exportReceptionUserTemplatePhrase(id: string, isJsonFile: boolean): Promise<IContentSource> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    const query = {
      params: {
        isJsonFile: isJsonFile,
      }
    };
    const result = await this.get("/api/templatephrase/reception/user/" + id + "/export", query);
    return new FixedPhraseExportSource(() => this.token, result.data);
  }

  async exportReceptionDepartmentTemplatePhrase(id: string, isJsonFile: boolean): Promise<IContentSource> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    const query = {
      params: {
        isJsonFile: isJsonFile,
      }
    };
    const result = await this.get("/api/templatephrase/reception/department/" + id + "/export", query);
    return new FixedPhraseExportSource(() => this.token, result.data);
  }

  async exportAiUserFixedPhrase(id: string, isJsonFile: boolean): Promise<IContentSource> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    const query = {
      params: {
        isJsonFile: isJsonFile,
      }
    };
    const result = await this.get("/api/templatephrase/ai/user/" + id + "/export", query);
    return new FixedPhraseExportSource(() => this.token, result.data);
  }

  async exportAiDepartmentFixedPhrase(id: string, isJsonFile: boolean): Promise<IContentSource> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    const query = {
      params: {
        isJsonFile: isJsonFile,
      }
    };
    const result = await this.get("/api/templatephrase/ai/department/" + id + "/export", query);
    return new FixedPhraseExportSource(() => this.token, result.data);
  }

  async exportAiFormatFixedPhrase(id: string, isJsonFile: boolean): Promise<IContentSource> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    const query = {
      params: {
        isJsonFile: isJsonFile,
      }
    };
    const result = await this.get("/api/templatephrase/ai/format/" + id + "/export", query);
    return new FixedPhraseExportSource(() => this.token, result.data);
  }

  async exportAiCommonDepartmentFixedPhrase(isJsonFile: boolean): Promise<IContentSource> {
    const query = {
      params: {
        isJsonFile: isJsonFile,
      }
    };
    const result = await this.get("/api/templatephrase/ai/common-department/export", query);
    return new FixedPhraseExportSource(() => this.token, result.data);
  }
}
